<template>
  <div
    v-if="marketPosition"
    class="market-position"
    data-cy="market-position"
  >
    <!-- The band's endpoints, aligned over the bar's ends. The trailing "+" on the upper
         bound signals that winning bids extend above it - that's the number to beat.

         These percentiles are measured over auctions a call ACTUALLY ARRIVED ON, but the
         copy keeps saying "winning bids": on this UI a winning bid means a delivered call
         (owner decision, docs/MARKET_CONTEXT_DELIVERED_CALLS.md). Since R60 (2026-10-30) the
         Opportunity column beside it quotes this row's own P50 when it says why nothing is
         suggested; until then it showed a rank band on every buyer's typical bid, delivered or
         not, which contradicted the badge.

         A flat market (P25 and P75 on one cent) is one price, so it gets one label with no "+"
         and R30's words on hover (R56). Neither the range nor the bar renders without the
         quartiles, or for a bid too thinly traded to position (below) -->
    <template v-if="showsMarketRange">
      <div
        v-b-tooltip.hover
        class="market-position__range text-muted"
        :class="{ 'market-position__range--flat': isFlatMarket }"
        :title="priceRangeTooltip"
        data-cy="text-price-range"
      >
        <span v-if="isFlatMarket">{{ formatCurrency(marketPosition.marketP25InUSD) }}</span>
        <template v-else>
          <span>{{ formatCurrency(marketPosition.marketP25InUSD) }}</span>
          <span>{{ formatCurrency(marketPosition.marketP75InUSD) }}+</span>
        </template>
      </div>

      <div class="market-position__bar">
        <div
          class="market-position__band"
          :style="bandStyle"
        />
        <!-- No marker without a bid to mark: a dynamic campaign that placed no bids in the
             window still gets the market band, but planting a marker anywhere on it would
             read as a bid the campaign never made -->
        <div
          v-if="bidInUsd != null"
          class="market-position__marker"
          :class="tierTextClass"
          :style="markerStyle"
        />
      </div>
    </template>

    <div class="market-position__labels">
      <b-badge
        v-if="marketPosition.tier"
        :variant="getBadgeVariantForBidTier(marketPosition.tier)"
        data-cy="badge-bid-tier"
      >
        {{ getBidTierText(marketPosition.tier) }}
      </b-badge>
      <!-- A bid with no tier: too few delivered calls from other buyers to position it, in the
           row's window and in the last 7 days (R55, R57). A muted dash with no hover: buyer copy never
           says a market lacks activity or evidence, nor that a bid has no position (R64). The own-bid
           line still follows -->
      <span
        v-else-if="isUnpositioned"
        class="text-muted"
        data-cy="text-position-unavailable"
      >
        -
      </span>
      <span
        v-else
        class="text-muted"
        data-cy="text-no-bids"
      >
        {{ noBidsText }}
      </span>
      <!-- Same guard as the marker: a campaign that placed no bids in the window must not get
           a "Your bid" line either, for the same reason it gets no marker. With Slice H's
           suggestions on, the line carries the campaign's minimum too, and a bidder that varies
           its minimum breaks it down on hover (I23) -->
      <span
        v-if="bidInUsd != null"
        v-b-tooltip.hover
        class="market-position__own-bid"
        :title="termsBreakdownText || null"
        data-cy="text-own-bid"
      >
        {{ ownBidText }}
      </span>
    </div>
  </div>

  <div
    v-else
    class="text-muted"
  >
    -
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import {
  formatMarketWindow,
  getBadgeVariantForBidTier,
  getBidTierText,
  getCurrentTermsText,
  getMinimumText,
  getScopeMarketText,
  getTermsBreakdownText,
  toCents
} from '@/store/modules/market-context.store'

export default {
  name: 'MarketPositionIndicator',

  props: {
    // Market position row for one campaign, see MarketContextAPI.getCampaignMarketPositions
    marketPosition: {
      type: Object,
      default: null
    },

    // The bid to mark on the band - the row's positionedBidInUSD. Null when a dynamic
    // campaign placed no bids in the window, and then the band renders with no marker
    bidInUsd: {
      type: Number,
      default: null
    },

    // The window the percentiles and the no-bids verdict were measured over, as the `short`
    // form of formatMarketWindow ('24h' or '7d'). "No bids (24h)" and "No bids (7d)" are very
    // different statements about a campaign, so the band must not claim one when it means the
    // other
    windowShort: {
      type: String,
      default: ''
    },

    // Static campaigns configure a single fixed bid, so bidInUsd IS that number - "Your bid"
    // is exact. RTB/Programmatic campaigns don't set one; bidInUsd is the median of what they
    // actually placed in the window, so "Your bid" would overstate it as a fixed number.
    // "Your typical bid" says what it actually is. A fixed-bid Programmatic campaign's
    // bidInUsd is its static bid amount, which currentTerms words as "Bids", as for the rest
    isStatic: {
      type: Boolean,
      default: true
    },

    // Inside the rx-market-intelligence rollout (the list's showMarketIntelligence), the own-bid
    // line names what the campaign bids now, minimum included (§3.9, §4.6), from the row's
    // currentTerms. A row without them, from a backend older than Slice H, keeps today's line
    marketIntelligence: {
      type: Boolean,
      default: false
    },

    // Word the terms line: "Your bid" for a Static campaign, "Bids" for a RingbaRTB or
    // Programmatic one
    routingType: {
      type: String,
      default: ''
    },

    // A CPA-only category has no minimum, so the line names the bid alone
    isCpaOnly: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Bare until the window is known, rather than briefly claiming the wrong one
    noBidsText() {
      return this.windowShort ? `No bids (${this.windowShort})` : 'No bids'
    },

    // A bid E1 could not position: a tier of null beside a positioned bid. From the 2026-10-30
    // follow-ups E1 positions a bid on the delivered calls other buyers won (R55), and sends no tier
    // and no quartiles while those are under 30 in the row's window and in the last 7 days (R57).
    // Not below market, so the list's banner leaves it out. A tier of null with no bid is a dynamic
    // campaign that placed none, which keeps "No bids"
    isUnpositioned() {
      return this.bidInUsd != null && !this.marketPosition.tier
    },

    // The range and the bar need both ends of the middle half. They can be null on a row E1 could
    // not position (above), and today's rows always carry them
    showsMarketRange() {
      return !this.isUnpositioned
        && this.marketPosition.marketP25InUSD != null
        && this.marketPosition.marketP75InUSD != null
    },

    ownBidLabel() {
      return this.isStatic ? 'Your bid' : 'Your typical bid'
    },

    // What the campaign bids now, from E1's currentTerms (backend PR 2 on), under Slice H
    currentTerms() {
      return this.marketIntelligence && this.marketPosition?.currentTerms?.bidInUSD != null
        ? this.marketPosition.currentTerms
        : null
    },

    termsBreakdownText() {
      return this.currentTerms
        ? getTermsBreakdownText(this.currentTerms.byMinimum)
        : ''
    },

    // "Your bid $30.00 · 120 s", "Bids $25.00 at 90 s"; today's "Your bid $30.00" or "Your
    // typical bid $25.00" without the terms
    ownBidText() {
      if (!this.currentTerms) {
        return `${this.ownBidLabel} ${this.formatCurrency(this.bidInUsd)}`
      }

      return getCurrentTermsText(this.currentTerms, {
        routingType: this.routingType,
        isCpaOnly: this.isCpaOnly,
        varies: Boolean(this.termsBreakdownText)
      })
    },

    // "Winning" is load-bearing: the percentiles are measured over auction WINNERS, so a bare
    // "bids" would read as what campaigns like yours offer, a lower and less demanding number
    // than what it takes to win. Since 2026-09-04 those winners are additionally restricted to
    // auctions a call actually arrived on - but the wording stays, because on this UI a winning
    // bid means a delivered call. Names the window only once it is known, for the same reason
    // noBidsText does. A flat market is one price, so it takes R30's words, not a range from a
    // price to itself (R56): "Winning bids at a 90-second minimum in this market over the last 7
    // days were $12.00"
    priceRangeTooltip() {
      const low = this.formatCurrency(this.marketPosition.marketP25InUSD)
      const high = this.formatCurrency(this.marketPosition.marketP75InUSD)

      if (this.isFlatMarket) {
        return `Winning bids ${this.priceRangePopulationText} were ${low}`
      }

      return `Winning bids ${this.priceRangePopulationText} mostly fell between ${low} and ${high} - top campaigns bid above ${high}`
    },

    // Which winning bids the range is, and over what window. An older backend's row is the cell's
    // winning bids at every minimum over the list's window. From backend PR 1 a row carries the
    // calls it was positioned on (§3.7, I38): the market's standard minimum, in I12's words, and the
    // row's own window, E1's or, where that window is thin at the minimum, the last 7 days. It must
    // not say "last 24h" then. A row that fell back reads the 7 days of the scope that served the
    // standard, so it names a broader market when the ladder climbed. On a Monday both windows are
    // 7 days and a fallback cannot be told apart, so it stays "in this market".
    priceRangePopulationText() {
      const position = this.marketPosition

      if (typeof position.positionWindowHours !== 'number') {
        return `in this market${this.windowShort ? ` in the last ${this.windowShort}` : ''}`
      }

      const rowWindow = formatMarketWindow(position.positionWindowHours)
      const fellBack = Boolean(this.windowShort) && rowWindow.short !== this.windowShort

      return [
        getMinimumText(position.pricedOn),
        fellBack ? getScopeMarketText(position.scope) : 'in this market',
        `over the ${rowWindow.long}`
      ].filter(Boolean).join(' ')
    },

    // The bar spans half an interquartile range beyond P25/P75 on each side, so the
    // bid marker stays visible for bids slightly outside the market band
    scale() {
      const p25 = this.marketPosition.marketP25InUSD
      const p75 = this.marketPosition.marketP75InUSD
      const padding = Math.max((p75 - p25) * 0.5, 1)

      return {
        min: p25 - padding,
        max: p75 + padding
      }
    },

    // Thin cells hit this more often since the percentiles count delivered calls only. The one test
    // for a flat row, for the dot, the single label and the tooltip alike
    isFlatMarket() {
      return this.marketPosition.marketP25InUSD != null
        && toCents(this.marketPosition.marketP25InUSD) === toCents(this.marketPosition.marketP75InUSD)
    },

    bandStyle() {
      // A flat market has no interquartile range to fill, and a zero-width band reads as a
      // missing bar - draw a dot on the price instead, as BidMarketContextPanel does. The dot is
      // as wide as the bar is tall, which with the band's border radius makes it round.
      if (this.isFlatMarket) {
        return {
          left: `${this.positionPercent(this.marketPosition.marketP25InUSD)}%`,
          width: '6px',
          transform: 'translateX(-50%)'
        }
      }

      return {
        left: `${this.positionPercent(this.marketPosition.marketP25InUSD)}%`,
        width: `${this.positionPercent(this.marketPosition.marketP75InUSD) - this.positionPercent(this.marketPosition.marketP25InUSD)}%`
      }
    },

    // Only read when the marker renders, i.e. when bidInUsd is non-null - deliberately
    // no fallback bid, so a missing bid can never resolve to a marker position
    markerStyle() {
      return {
        left: `${this.positionPercent(this.bidInUsd)}%`
      }
    },

    tierTextClass() {
      return `text-${this.getBadgeVariantForBidTier(this.marketPosition.tier)}`
    }
  },

  methods: {
    getBadgeVariantForBidTier,

    getBidTierText,

    formatCurrency,

    positionPercent(value) {
      const percent = ((value - this.scale.min) / (this.scale.max - this.scale.min)) * 100
      return Math.min(100, Math.max(0, percent))
    }
  }
}
</script>

<style lang="scss" scoped>
.market-position__bar {
  position: relative;
  width: 150px;
  height: 6px;
  border-radius: 3px;
  background-color: $violet-dark;
}

.market-position__band {
  position: absolute;
  top: 0;
  height: 6px;
  border-radius: 3px;
  background-color: rgba($blue, 0.45);
}

.market-position__marker {
  position: absolute;
  top: -3px;
  width: 2px;
  height: 12px;
  // Inherits the tier's Bootstrap text-* color so it always matches the badge
  background-color: currentColor;
}

.market-position__labels {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  white-space: nowrap;

  // A phone's campaigns list is TouchTable's cards, whose value column is narrower than the badge
  // and the own-bid line together ("Bids $41.50 · minimum not reported" ran about 130px past the
  // card), so the line wraps under the badge there rather than scroll the card sideways
  @include media-breakpoint-down(sm) {
    flex-wrap: wrap;
    white-space: normal;
  }
}

// Deliberately not text-muted while __range is: the buyer's own number is the one they
// came to read, so it wins the contrast against the market reference
.market-position__own-bid {
  font-size: 0.75rem;
}

// Same width as the bar so the two labels align with its ends
.market-position__range {
  display: flex;
  justify-content: space-between;
  width: 150px;
  margin-bottom: 2px;
  font-size: 0.75rem;
}

// A flat market's one label sits over its dot, which the scale centres on the price
.market-position__range--flat {
  justify-content: center;
}
</style>
